import { all, dropTask } from 'ember-concurrency';
import Button from 'tio-reimagined/components/button';
import { flatten, safeParse } from 'valibot';
import { fn } from '@ember/helper';
import { Header, HStack, VStack } from 'tio-ui/components/layout';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { setSemesterCodeString } from 'tio-common/utils/format';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
  setSingleValueForTasField,
  getConfigForTasField,
  getFieldLabelForFieldName,
  type FieldName,
  type FieldValue,
  type TemplateFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { LinkTo } from '@ember/routing';
import { trackedObject } from '@ember/reactive/collections';
import AddEducationDrawer from 'tio-employee/components/tas/add-education-drawer';
import type { ValidationErrorSignature } from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import AddScholarshipsDrawer from 'tio-common/components/tas/add-scholarships-drawer';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import { getInputmaskAlias, getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import type {
  CustomFieldSignature,
  ReceivedScholarship,
} from 'tio-common/types/tuition-assistance';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-common/models/tas-program-instance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-common/models/tas-program-template';
import type { Touched } from 'tio-common/helpers/errors-for-valibot-field';
import type ConditionModel from 'tio-common/models/condition';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type StoreService from 'ember-data/store';
import type TasApplicationAdapter from 'tio-employee/adapters/tas-application';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasParticipantAdapter from 'tio-employee/adapters/tas-participant';
import type TasProgramInstanceAdapter from 'tio-employee/adapters/tas-program-instance';
import type TasCourseModel from 'tio-common/models/tas-course';
import type TASProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type TasProgramTemplateInstitutionModel from 'tio-common/models/tas-program-template-institution';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type TasUnifiedInstitutionModel from 'tio-common/models/tas-unified-institution';
import type Owner from '@ember/owner';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type AgreementModel from 'tio-common/models/agreement';
import PaymentMethodAndSubtotals from '../../payment-method-and-subtotals';
import type IntlService from 'ember-intl/services/intl';
import { PhCaretLeft, PhChatCenteredText, PhTrash } from 'ember-phosphor-icons';
import ApplicationHistoryTimeline from 'tio-common/components/tas/application-history-timeline';
import PartnerMessagingDrawer from 'tio-employee/components/drawers/partner-messaging-drawer';
import type LpnPage from 'tio-common/models/lpn-page';
import LpnInfoCard from 'tio-common/components/lpn/lpn-info-card';
import type PartnerService from 'tio-employee/services/partner';
import { normalizeDate } from 'tio-common/utils/date/normalize-date';
import type { TasUnifiedInstitution } from 'tio-common/models/search';
import { formatDateToISO } from 'tio-common/utils/date';

import ProgressTracker from './progress-tracker';
import StepEducationRequest from './step-education-request';
import StepExpenseItems from './step-expense-items';
import StepAttachments from './step-attachments';
import StepPaymentType from './step-payment-type';
import StepSubmit from './step-submit';
import CompletedStepSummary from './completed-step-summary';

type ProgramInstanceFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FormField = {
  name: ProgramInstanceFieldName;
  mask: string | null;
  type: string | null;
  rules: {
    type: string;
    typeError?: string;
  };
  disabled: boolean;
  fieldOrigination: 'instance' | 'application';
};

interface Scholarship extends ReceivedScholarship {
  id?: string;
}

export interface StepDefinition {
  id: string;
  label: string;
  timeEstimate: string;
  isApplicable: boolean;
  isComplete: boolean;
  summaryText: string;
  summaryKind?: 'course-cash';
}

export interface GuidedWizardSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      programInstance: TASProgramInstanceModel;
      selectedSchool?: TasUnifiedInstitutionModel;
    };
    typeClassification?: string;
  };
}

export default class GuidedWizard extends Component<GuidedWizardSignature> {
  programTemplate: TasProgramTemplateModel;
  instanceFields: TASProgramInstanceModelFieldsSignature;

  constructor(owner: Owner, args: GuidedWizardSignature['Args']) {
    super(owner, args);
    const { application } = args.model;
    this.programTemplate = application.tasProgramInstance.tasProgramTemplate;

    if (this.programTemplate.isDirectPayProgram) {
      const existingMethod = getSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        application.fields,
        ''
      );
      if (!existingMethod) {
        setSingleValueForTasField(
          'PAYMENT_TYPE_FOR_DIRECT_PAY',
          'TAS.AssistanceManner.COURSE_CASH',
          application.fields
        );
      }
    }

    if (this.showGPAField) {
      this.setGPA(getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || '0');
    }
    if (this.requiresDateEligibilityCheck) {
      this.updateEligibility();
    }
    this.setSelectedSchoolIfExists();
    this.instanceCustomFields = application.tasProgramInstance.customFields;
    this.applicationCustomFields = application.customFields;
    this.instanceFields = trackedObject(application.tasProgramInstance.fields);
    this.applicationFields = trackedObject(application.fields);
    this.updateCustomRequiredFields();
    this.initializePaymentMethod();
    this.endDate = getSingleValueForTasField('COURSES_END_DATE', this.applicationFields, '');
    this.startDate = getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields, '');
    this.initializeLimitsIfNeeded();
    if (!!this.startDate) {
      this.touched = { dateEntered: true };
    } else {
      this.touched = { dateEntered: false };
    }

    // Resume from the first incomplete step and initialize the furthest jump target to that step.
    const initialStep = this.computeInitialStep();
    this.currentStep = initialStep;
    this.furthestVisitedStepIndex = initialStep;
  }

  // --- Tracked properties ---
  @tracked currentStep = 0;
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked course: TasCourseModel | null = null;
  @tracked eligibility = true;
  @tracked hasSubmitted = false;
  @tracked isDeleteApplicationOpen = false;
  @tracked isDeleteCourseOpen = false;
  @tracked isDeleteScholarshipOpen = false;
  @tracked isEducationOpen = false;
  @tracked isScholarshipOpen = false;
  @tracked scholarshipIdToEdit?: string;
  @tracked scholarshipToDelete?: Scholarship;
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked submitError: string | null = null;
  @tracked instanceCustomFields: CustomFieldSignature[] = [];
  @tracked applicationCustomFields: CustomFieldSignature[] = [];
  @tracked gpaBasedAmount = 0;
  @tracked requiredCustomFieldsFilled = false;
  @tracked paymentMethod = 'TAS.AssistanceManner.REIMBURSEMENT';
  @tracked showPaymentMethodModal = false;
  @tracked isPartnerMessagingDrawerOpen = false;
  @tracked isLpnSchoolSelected = false;
  @tracked endDate = '';
  @tracked startDate = '';
  applicationFields!: TasApplicationModel['fields'];
  @tracked touched: Touched = { dateEntered: false };
  @tracked shouldFetchLimits = false;
  @tracked dynamicEligibility?: boolean;
  @tracked programEligibility: [string, string][] = [];
  @tracked attemptedSteps = new Set<number>();
  @tracked furthestVisitedStepIndex = 0;

  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare partner: PartnerService;

  // --- Application History ---
  get hasMeaningfulHistory() {
    const history = this.application.fullHistory;
    if (!history.length) return false;
    // Don't show if the only entry is the initial CREATED event
    return history.some(
      (item) =>
        item.logType !== 'state_transition' ||
        (item as { stateTransitionType?: string }).stateTransitionType !== 'CREATED'
    );
  }

  // --- Partner Messaging ---
  get showPartnerMessaging() {
    return !!this.programTemplate?.company?.companySetting?.tuitionAssistance
      ?.isPartnerMessagingEnabled;
  }

  get numberOfUnreadMessages() {
    const partnerMessages = this.application.comments?.filter((comment) =>
      comment.tags?.includes('Partner Messaging')
    );
    const messageCount = partnerMessages?.length ?? 0;
    return messageCount > 0 ? messageCount : '';
  }

  openPartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = true;
  };

  closePartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = false;
  };

  // --- Type guards ---
  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  private hasValidInstitutionId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { institutionId: string } {
    return !!(institution && 'institutionId' in institution && institution.institutionId);
  }

  private hasValidLocationName(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { locationName: string } {
    return !!(institution && 'locationName' in institution && institution.locationName);
  }

  // --- Step management ---
  get typeClassification() {
    return this.args.typeClassification || this.programTemplate.typeClassification || '';
  }

  get isTypeTwoProgram() {
    return this.typeClassification === 'TAS.ProgramType.2';
  }

  get isTypeFourProgram() {
    return (
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.4.3'
    );
  }

  get isTypeFourTwo() {
    return this.typeClassification === 'TAS.ProgramType.4.2';
  }

  get isTypeFourThree() {
    return this.typeClassification === 'TAS.ProgramType.4.3';
  }

  get allStepDefinitions(): StepDefinition[] {
    const appName = getSingleValueForTasField('APPLICATION_NAME', this.applicationFields) || '';
    const courseCount = this.application.activeCourses?.length || 0;
    const assetCount = this.application.attachments?.length || 0;

    return [
      {
        id: 'education-request',
        label: this.intl.t('tas.guided.steps.education_request'),
        timeEstimate: '2-3 min',
        isApplicable: true,
        isComplete: this.isEducationRequestComplete,
        summaryText: appName as string,
      },
      {
        id: 'expense-items',
        label: this.expenseTypeField,
        timeEstimate: '5-7 min',
        isApplicable: true,
        isComplete: courseCount > 0,
        summaryText: courseCount
          ? `${this.formatMoney(this.application.requestedTotal)} total`
          : '',
      },
      {
        id: 'attachments',
        label: this.intl.t('common.attachments'),
        timeEstimate: '3-5 min',
        isApplicable: this.shouldShowAttachments,
        isComplete: !this.isAttachmentRequired || this.hasRequiredAttachments,
        summaryText: assetCount ? `${assetCount} files included` : '',
      },
      {
        id: 'payment-type',
        label: this.intl.t('tas.guided.steps.payment_type'),
        timeEstimate: '3-5 min',
        isApplicable: true,
        isComplete: this.programTemplate.isDirectPayProgram ? !!this.paymentMethod : true,
        summaryText: this.paymentMethodSummary,
        summaryKind:
          this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' ? 'course-cash' : undefined,
      },
      {
        id: 'submit',
        label: this.intl.t('tas.guided.steps.submit'),
        timeEstimate: '1-2 min',
        isApplicable: true,
        isComplete: false,
        summaryText: '',
      },
    ];
  }

  get applicableSteps() {
    return this.allStepDefinitions.filter((s) => s.isApplicable);
  }

  get currentStepDef() {
    return this.applicableSteps[this.currentStep];
  }

  get isEducationRequestComplete() {
    const hasAppName = !!getSingleValueForTasField('APPLICATION_NAME', this.applicationFields);
    if (!hasAppName || !this.eligibility || this.hasStartDateValidationErrors) {
      return false;
    }

    // Check end date if required
    if (this.endDateConfig.visible && this.endDateConfig.required && !this.endDate) {
      return false;
    }

    // Check all required program form fields (institution, specialization, etc.)
    for (const programField of this.programFormFields) {
      if (programField.disabled || programField.name === 'APPLICATION_NAME') continue;
      const config = getConfigForTasField(
        programField.name as TemplateFieldName,
        this.programTemplate.fields
      );
      if (!config.visible || !config.required) continue;
      const fieldsSource =
        programField.fieldOrigination === 'application'
          ? this.applicationFields
          : this.instanceFields;
      const value = getSingleValueForTasField(programField.name as FieldName, fieldsSource);
      if (!value) return false;
    }

    // Check required custom fields
    if (this.customFieldValidationErrors.length > 0) {
      return false;
    }

    return true;
  }

  isCustomFieldRequired = (field: CustomFieldSignature) => {
    // Template is source of truth for required; fallback to instance value
    const templateField = this.programTemplate.customFields?.find((f) => f.id === field.id);
    return templateField?.required ?? field.required;
  };

  get customFieldValidationErrors(): ValidationErrorSignature[] {
    const allCustomFields = [...this.instanceCustomFields, ...this.applicationCustomFields];
    const errors: ValidationErrorSignature[] = [];
    for (const field of allCustomFields) {
      if (this.isCustomFieldRequired(field)) {
        const val = field.values[0];
        if (val === null || val === undefined || val === '') {
          errors.push({
            path: field.label,
            errors: [`${field.label} is required`],
            value: '',
          });
        }
      }
    }
    return errors;
  }

  get paymentMethodSummary() {
    if (this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      return 'CourseCash';
    }
    if (this.paymentMethod === 'TAS.AssistanceManner.REIMBURSEMENT') {
      return 'Reimbursement';
    }
    if (!this.programTemplate.isDirectPayProgram) {
      return 'Reimbursement';
    }
    return '';
  }

  computeInitialStep() {
    // New applications with no courses should always start at step 0
    const courseCount = this.application.activeCourses?.length || 0;
    const isNewApplication =
      this.application.state === 'TAS.ApplicationState.CREATED' ||
      this.application.state === 'TAS.ApplicationState.DEFINE_COURSES';
    if (isNewApplication && courseCount === 0) {
      return 0;
    }

    const steps = this.applicableSteps;
    for (let i = 0; i < steps.length - 1; i++) {
      if (!steps[i]!.isComplete) {
        return i;
      }
    }
    return Math.max(0, steps.length - 1);
  }

  isStepPassed = (index: number) => {
    return index < this.currentStep;
  };

  isStepPassedAndComplete = (index: number) => {
    return index < this.currentStep && (this.applicableSteps[index]?.isComplete ?? false);
  };

  isStepPassedAndIncomplete = (index: number) => {
    return index < this.currentStep && !(this.applicableSteps[index]?.isComplete ?? false);
  };

  hasStepBeenAttempted = (index: number) => {
    return this.attemptedSteps.has(index);
  };

  advanceStep = () => {
    const updated = new Set(this.attemptedSteps);
    updated.add(this.currentStep);
    this.attemptedSteps = updated;
    this.saveTask.perform();
    const nextStep = Math.min(this.currentStep + 1, this.applicableSteps.length - 1);
    this.currentStep = nextStep;
    this.furthestVisitedStepIndex = Math.max(this.furthestVisitedStepIndex, nextStep);
  };

  canGoToStep = (stepIndex: number) => {
    return (
      stepIndex >= 0 &&
      stepIndex < this.applicableSteps.length &&
      stepIndex <= this.furthestVisitedStepIndex
    );
  };

  goToStep = (stepIndex: number) => {
    if (this.canGoToStep(stepIndex)) {
      this.currentStep = stepIndex;
    }
  };

  isReopenableFutureStep = (index: number) => {
    return index > this.currentStep && this.canGoToStep(index);
  };

  // --- Core getters (migrated from type-two-or-three) ---
  get application() {
    return this.args.model.application;
  }

  get instance() {
    return this.args.model.programInstance;
  }

  get applicationName() {
    return getSingleValueForTasField('APPLICATION_NAME', this.applicationFields);
  }

  get expenseTypeField() {
    const expenseType = getSingleValueForTasField(
      'EXPENSE_TYPE',
      this.programTemplate.fields
    ) as string;
    return expenseType || 'Expense Items';
  }

  get conditionsForSubmit(): readonly ConditionModel[] {
    // Type 4 programs handle programApproveConditions at enrollment,
    // so only show application-level conditions here.
    if (this.typeClassification === 'TAS.ProgramType.4.2') {
      return this.programTemplate.typeTwoApplicationConditions || [];
    }
    if (this.typeClassification === 'TAS.ProgramType.4.3') {
      return this.programTemplate.applicationApproveCoursesConditions || [];
    }

    // For Type 2/3, include programApproveConditions as a legacy fallback.
    // Some programs never migrated their conditions from program_approve_workflow
    // to application_approve_courses/evidence_workflow provinces.
    const programConditions = this.programTemplate.programApproveConditions || [];

    const conditionsMap: Record<string, readonly ConditionModel[]> = {
      'TAS.ProgramType.2': this.programTemplate.typeTwoApplicationConditions,
      'TAS.ProgramType.3': this.programTemplate.applicationApproveCoursesConditions,
    };
    const applicationConditions = conditionsMap[this.typeClassification] || [];

    return [...programConditions, ...applicationConditions];
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  get shouldShowAttachments() {
    if (this.isTypeFourProgram) {
      return (
        this.programTemplate.attachmentsForCourseApprovalVisible ||
        this.programTemplate.attachmentsForCourseCompletionVisible
      );
    }
    if (this.isTypeTwoProgram) {
      return (
        this.programTemplate.attachmentsForCourseApprovalVisible ||
        this.programTemplate.attachmentsForCourseCompletionVisible
      );
    }
    return this.programTemplate.attachmentsForCourseApprovalVisible;
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    if (this.isTypeTwoProgram || this.isTypeFourProgram) {
      return (
        this.programTemplate.requireAttachmentsForCourseApproval ||
        this.programTemplate.requireAttachmentsForCourseCompletion
      );
    }
    return this.programTemplate.requireAttachmentsForCourseApproval;
  }

  get attachmentClassificationString() {
    if (this.isTypeTwoProgram || this.isTypeFourTwo) {
      return 'COMPLETION_DOCUMENTS';
    }
    return 'COURSE_APPROVAL_DOCUMENTS';
  }

  get attachmentsListItems() {
    const { coursePreApproval, courseCompletion } = this.programTemplate.attachmentListItems;

    if (this.isTypeFourThree) {
      return coursePreApproval;
    }
    if (this.isTypeFourTwo || this.isTypeTwoProgram) {
      return [coursePreApproval, courseCompletion].flat();
    }
    return coursePreApproval;
  }

  get attachmentsListInstructions() {
    const { coursePreApproval, courseCompletion } = this.programTemplate.attachmentInstructions;

    let itemsToInclude;
    if (this.isTypeFourThree) {
      itemsToInclude = [coursePreApproval].filter(Boolean);
    } else if (this.isTypeFourTwo || this.isTypeTwoProgram) {
      itemsToInclude = [coursePreApproval, courseCompletion].filter(Boolean);
    } else {
      itemsToInclude = [coursePreApproval].filter(Boolean);
    }

    return itemsToInclude.length ? itemsToInclude.join(', ') : '';
  }

  get lockStatus() {
    const lockType: Record<string, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]!
      : '';
  }

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn;
  }

  get requiresDateEligibilityCheck() {
    const locksRequired = [
      'COURSE_START_DATE',
      'COURSE_END_DATE',
      'ON_COURSES_SUBMISSION',
      'ON_SUBMISSION',
    ];
    return (
      locksRequired.includes(this.lockEmployeeStatusBasedOn) &&
      !this.application.applicationSubmittedDate
    );
  }

  get scholarships() {
    const savedScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.application.fields
    );
    return savedScholarships.map((scholarship, index) => ({
      ...(scholarship as ReceivedScholarship),
      id: `scholarship-${index + 1}`,
    }));
  }

  get scholarshipName() {
    return this.programTemplate.fields['SCHOLARSHIP_NAME'];
  }

  get scholarshipAmount() {
    return this.programTemplate.fields['SCHOLARSHIP_AMOUNT'];
  }

  get scholarshipReceived() {
    return this.programTemplate.fields['SCHOLARSHIPS_RECEIVED'];
  }

  get customEmployeeFields(): CustomFieldSignature[] {
    return this.instanceCustomFields.filter((field) => field.province === 'EMPLOYEE');
  }

  get customProgramFields(): CustomFieldSignature[] {
    return this.instanceCustomFields.filter((field) => field.province === 'PROGRAM');
  }

  get customApplicationFields(): CustomFieldSignature[] {
    return this.applicationCustomFields.filter((field) => field.province === 'APPLICATION');
  }

  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  get programFormFields(): FormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      {
        name: 'APPLICATION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_ID',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'FT_PT_OTHER',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: true,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_DOB',
        mask: null,
        type: 'date',
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'EMPLOYEE_EMAIL',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'SCHOOL_INSTITUTION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'PROGRAM_SPECIALIZATION',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'PROGRAM_MAJOR',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ESTIMATED_COST',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ENROLLMENT_OBJECTIVE',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'STUDENT_ID',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
        fieldOrigination: 'application',
      },
      {
        name: 'ESTIMATED_PROGRAM_BEGIN',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
        fieldOrigination: 'instance',
      },
      {
        name: 'ESTIMATED_PROGRAM_COMPLETION',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
        fieldOrigination: 'instance',
      },
    ];
  }

  get startDateConfig() {
    return getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
  }

  get endDateConfig() {
    return getConfigForTasField('COURSES_END_DATE', this.programTemplate.fields);
  }

  get startDateLabel() {
    return getFieldLabelForFieldName('COURSES_BEGIN_DATE', this.programTemplate.fields) as string;
  }

  get endDateLabel() {
    return getFieldLabelForFieldName('COURSES_END_DATE', this.programTemplate.fields) as string;
  }

  get isActiveParticipant() {
    return this.instance.employee?.tasParticipant?.isActive || false;
  }

  get isClaimsFinancialsApplication() {
    return this.programTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get showGPAField() {
    return this.calculateTotalByGPA && this.isTypeTwoProgram;
  }

  get startOrEndDate(): string {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.applicationFields);
    }
    return new Date().toLocaleDateString('en-CA');
  }

  get startDateValidationErrors() {
    const startDateConfig = getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
    if (!startDateConfig.visible) {
      return {};
    }
    const schema = this.tuitionAssistance.dynamicBeginDateValidationSchema(
      this.programTemplate,
      this.endDate
    );
    const model = {
      dateEntered: new Date(this.startDate),
    };
    const result = safeParse(schema, model, { abortEarly: false });
    if (!result.success) {
      const flattened = flatten(result.issues);
      return flattened.nested ?? {};
    }
    return {};
  }

  get hasStartDateValidationErrors(): boolean {
    return Object.keys(this.startDateValidationErrors || {}).length > 0;
  }

  get eligibleBasedOnWaitingPeriod() {
    return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant,
      this.startDate || undefined
    );
  }

  get eligibilityWaitingPeriod() {
    return this.programTemplate.eligibilityWaitingPeriod as number;
  }

  get dateEligible() {
    const date = this.tuitionAssistance.waitingPeriodEndDate(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant
    );
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get hasGpaIfRequired(): boolean {
    if (!this.showGPAField) return true;
    return Boolean(getSingleValueForTasField('GRADE_POINT_AVERAGE', this.applicationFields));
  }

  get requiredFieldsSet() {
    const fieldsToCheck = [
      ...this.programFormFields,
      { name: 'COURSES_BEGIN_DATE', disabled: false, fieldOrigination: 'application' },
      { name: 'COURSES_END_DATE', disabled: false, fieldOrigination: 'application' },
      { name: 'EMPLOYEE_DOB', disabled: false, fieldOrigination: 'instance' },
    ];

    return fieldsToCheck.every((field) => {
      const fieldConfig = getConfigForTasField(
        field.name as TemplateFieldName,
        this.programTemplate.fields
      );
      if (!fieldConfig.required || field?.disabled === true) {
        return true;
      }
      const fieldsSource =
        'fieldOrigination' in field && field.fieldOrigination === 'application'
          ? this.applicationFields
          : this.instanceFields;
      const value = getSingleValueForTasField(field.name as FieldName, fieldsSource);
      return value !== null && value !== undefined && value !== '';
    });
  }

  get hasRequiredAttachments() {
    // When categorized upload is active (the program template defines required
    // document entries for this step's classification), every configured entry
    // type must be covered by at least one non-discarded asset. `general` rows
    // require upload presence only; AI-classified rows require a valid or
    // accepted validation outcome.
    const documentEntries = this.programTemplate.documentEntriesFor(
      this.attachmentClassificationString
    );
    const requiredTypes = [...new Set(documentEntries.map((entry) => entry.type))];
    if (requiredTypes.length > 0) {
      const liveAssets = this.application.tasAssets.filter(
        (a) => !a.attachmentDeletedAt && a.classification === this.attachmentClassificationString
      );
      return requiredTypes.every((type) => {
        const bucketAssets = liveAssets.filter((asset) => asset.employeeSelectedType === type);
        if (bucketAssets.length === 0) return false;
        if (type === 'general') return true;

        // Every failed file in a required bucket must be discarded or accepted;
        // a separate valid file should not hide unresolved warnings from submit.
        const hasUnresolvedAsset = bucketAssets.some((asset) =>
          ['flagged', 'pending', 'error'].includes(asset.aiValidationStatus ?? '')
        );
        if (hasUnresolvedAsset) return false;

        return bucketAssets.some((asset) => {
          const status = asset.aiValidationStatus;
          if (status === 'accepted') return true;
          return status === 'valid' && asset.satisfiesRequiredType(type);
        });
      });
    }

    // Legacy (non-categorized) check: at least one attachment exists.
    if (this.isTypeTwoProgram || this.isTypeFourProgram) {
      return (
        this.application.hasRequiredEvidenceAttachments ||
        this.application.hasRequiredPreApprovalAttachments
      );
    }
    // Type 3
    return this.application.hasRequiredPreApprovalAttachments;
  }

  get setRequestedTotalAmount() {
    if (this.isClaimsFinancialsApplication) return 0;
    if (!this.showGPAField) return this.application.requestedTotal;
    return Math.min(this.gpaBasedAmount, this.application.requestedTotal);
  }

  get approvedTotalString() {
    return this.isTypeTwoProgram ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
  }

  get canSubmitApplication() {
    const requiredState = 'TAS.ApplicationState.DEFINE_COURSES';
    return (
      this.isActiveParticipant &&
      !!this.application.activeCourses.length &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      this.application.state === requiredState &&
      this.requiredFieldsSet &&
      this.eligibility &&
      this.requiredCustomFieldsFilled &&
      this.eligibleBasedOnWaitingPeriod &&
      this.hasGpaIfRequired &&
      !this.hasStartDateValidationErrors
    );
  }

  get adapterMethod() {
    const actionMap: Record<string, string> = {
      'TAS.ProgramType.2': 'requestCourseEvidence',
      'TAS.ProgramType.3': 'requestCoursesApproval',
      'TAS.ProgramType.4.2': 'requestCourseEvidence',
      'TAS.ProgramType.4.3': 'requestCoursesApproval',
    };
    return actionMap[this.typeClassification] || '';
  }

  // --- Initialization helpers ---
  initializeLimitsIfNeeded() {
    const basedOn = this.programTemplate.calculateTowardsLimitBasedOn;
    const hasSetDate =
      (basedOn === 'COURSE_START_DATE' && !!this.startDate) ||
      (basedOn === 'COURSE_END_DATE' && !!this.endDate);
    if (!hasSetDate) return;
    this.shouldFetchLimits = true;
  }

  calculateEligibility() {
    if (!this.programEligibility.length) return false;
    const enteredDate =
      this.lockStatus === 'submission' ? new Date() : new Date(this.startOrEndDate);
    return this.programEligibility.some(([start, end]) => {
      const startDate = new Date(start!);
      const endDate = new Date(end!);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  async updateEligibility(): Promise<void> {
    if (
      this.application.ignorePolicies ||
      this.lockStatus === 'fulfilled' ||
      !this.lockStatus ||
      !this.application.applicationSubmittedDate
    ) {
      this.eligibility = true;
      return;
    }
    try {
      if (!this.dynamicEligibility || !this.programEligibility.length) {
        const participantId = this.instance.employee.tasParticipant?.id;
        const { code } = this.programTemplate;

        if (!participantId) {
          // No participant record ⇒ no eligibility can be determined.
          this.eligibility = false;
          return;
        }

        const participantAdapter = this.store.adapterFor(
          'tas-participant'
        ) as unknown as TasParticipantAdapter;
        // Passing a program code makes the adapter return the per-program value
        // (boolean / [string,string][]) rather than the full map, but it can
        // still be undefined if the key is missing. Normalise to safe defaults.
        const [fetchDynamicEligibility, fetchProgramEligibility] = await Promise.all([
          participantAdapter.dynamicEligibility(participantId, code),
          participantAdapter.periodOfEligibility(participantId, code),
        ]);
        this.dynamicEligibility = Boolean(fetchDynamicEligibility);
        this.programEligibility = Array.isArray(fetchProgramEligibility)
          ? (fetchProgramEligibility as [string, string][])
          : [];
      }
      this.eligibility = this.dynamicEligibility! && this.calculateEligibility();
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      this.eligibility = false;
    }
  }

  setSelectedSchoolIfExists() {
    if (this.args.model.selectedSchool) {
      this.selectedSchool = this.args.model.selectedSchool;
    }
  }

  initializePaymentMethod() {
    if (!this.programTemplate.isDirectPayProgram) return;
    const method = getSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.applicationFields,
      ''
    );
    this.paymentMethod = method || 'TAS.AssistanceManner.COURSE_CASH';
  }

  // --- Field update callbacks ---
  updateAppName = (value: string) => {
    setSingleValueForTasField('APPLICATION_NAME', value, this.applicationFields);
  };

  updateDateFieldValue = (fieldName: FieldName, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      // @ts-expect-error - FieldName not fully compatible with setSingleValueForTasField signature
      setSingleValueForTasField(fieldName, isoDate, this.instanceFields);
    }
  };

  saveStartDate = async (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      this.startDate = formatDateToISO(selectedDates[0]);
      setSingleValueForTasField('COURSES_BEGIN_DATE', this.startDate, this.applicationFields);
      this.touched = { dateEntered: true };
      if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_START_DATE') {
        this.application.fields = this.applicationFields;
        await this.application.save();
        this.shouldFetchLimits = true;
        if (!this.hasStartDateValidationErrors) {
          this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
        }
      }
      if (this.lockEmployeeStatusBasedOn === 'COURSE_START_DATE') {
        this.updateEligibility();
      }
    }
  };

  saveEndDate = async (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      this.endDate = formatDateToISO(selectedDates[0]);
      setSingleValueForTasField('COURSES_END_DATE', this.endDate, this.applicationFields);
      if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_END_DATE') {
        this.application.fields = this.applicationFields;
        await this.application.save();
        this.shouldFetchLimits = true;
        this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
      }
      if (this.lockEmployeeStatusBasedOn === 'COURSE_END_DATE') {
        this.updateEligibility();
      }
    }
  };

  updateValueForField = (
    fieldName: FieldName,
    programField: FormField,
    value: string[] | string | number,
    event?: Event | undefined
  ) => {
    let valueToSet = Array.isArray(value) ? value[0] : value;
    if (getInputmaskAlias(event) === 'currency') {
      valueToSet = Number(getInputmaskUnmaskedValue(event)) * 100;
    }
    if (programField.type === 'number') {
      valueToSet = Math.round(Number(valueToSet));
    }
    if (programField.type === 'date' && typeof valueToSet === 'string') {
      valueToSet = normalizeDate(valueToSet as string);
    }
    const fields =
      programField.fieldOrigination === 'instance' ? this.instanceFields : this.applicationFields;
    // @ts-expect-error: fieldName
    setSingleValueForTasField(fieldName, valueToSet, fields);
  };

  inputValueForField = (field: FieldName, programField: FormField) => {
    const fields =
      programField.fieldOrigination === 'instance' ? this.instanceFields : this.applicationFields;
    return getSingleValueForTasField(field, fields) as string;
  };

  getAllValuesForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    return getAllValuesForTasField(
      fieldName as keyof TASProgramInstanceModelFieldsSignature,
      this.instanceFields
    ) as string[];
  };

  getSingleValueForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    const values = this.getAllValuesForField(fieldName);
    return values[0] || '';
  };

  setGPA = (value: string) => {
    const gpa = Number(value);
    setSingleValueForTasField('GRADE_POINT_AVERAGE', gpa, this.applicationFields);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
  };

  didSelectSchool = async (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;
    if (!searchResult) {
      this.isLpnSchoolSelected = false;
      return;
    }
    const isLpnSchool = await this.isSelectedSchoolLpn(searchResult);
    this.isLpnSchoolSelected = isLpnSchool;

    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.hasValidLocationName(searchResult) ? searchResult.locationName : '',
      this.instanceFields
    );
    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      this.hasValidInstitutionId(searchResult) ? searchResult.institutionId : '',
      this.instanceFields
    );
    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      this.hasValidDapipId(searchResult) ? searchResult.dapipId : '',
      this.instanceFields
    );
  };

  isSelectedSchoolLpn = async (searchResult: TasUnifiedInstitution) => {
    const lpnSettings = this.partner.companySettings.lpn;
    const isEnabled = lpnSettings?.isEnabled;
    if (!isEnabled) return false;
    if (!this.hasValidDapipId(searchResult)) return false;
    const pages = await this.store.query<LpnPage>('lpn-page', {
      filter: { 'dapip-id': searchResult.dapipId },
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
    });
    if (pages.length === 0) return false;
    const setting = await pages[0]?.settingForCompany(this.partner.companySettings.id);
    if (!setting) return true;
    return setting.isEnabled;
  };

  featuredSchoolData = trackedFunction(this, async () => {
    const template = this.args.model.programInstance.tasProgramTemplate;
    const schools = await this.store.query<TasProgramTemplateInstitutionModel>(
      'tas-program-template-institution',
      {
        filter: {
          'is-featured': true,
          'tas-program-template': template.id,
        },
        include: 'accredited-institution,unaccredited-institution,logo',
      }
    );
    return schools;
  });

  get featuredSchools() {
    return this.featuredSchoolData.value || [];
  }

  // --- Custom fields ---
  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    if (!existingFields) return updatedFields;
    updatedFields.forEach((newObject) => {
      const index = existingFields.findIndex(
        (item) => item.label === newObject.label && item.province === newObject.province
      );
      if (index === -1) {
        existingFields.push(newObject);
      } else {
        existingFields.splice(index, 1, newObject);
      }
    });
    return existingFields;
  };

  updateCustomRequiredFields = () => {
    const customFields = [...this.instanceCustomFields, ...this.applicationCustomFields];
    this.requiredCustomFieldsFilled = customFields.every((field) => {
      if (this.isCustomFieldRequired(field)) {
        return field.values[0] !== null && field.values[0] !== undefined && field.values[0] !== '';
      }
      return true;
    });
  };

  didUpdateApplicationCustomFields = async (customFields: CustomFieldSignature[]) => {
    const updatedFields = this.updateFieldsWithNewValues(
      this.applicationCustomFields,
      customFields
    );
    this.applicationCustomFields = updatedFields;
    this.application.customFields = updatedFields;
    this.updateCustomRequiredFields();
  };

  didUpdateEmployeeCustomFields = async (customFields: CustomFieldSignature[]) => {
    this.updateFieldsWithNewValues(this.instanceCustomFields, customFields);
    this.instanceCustomFields = [...this.instanceCustomFields];
    this.updateCustomRequiredFields();
  };

  didUpdateProgramCustomFields = async (customFields: CustomFieldSignature[]) => {
    this.updateFieldsWithNewValues(this.instanceCustomFields, customFields);
    this.instanceCustomFields = [...this.instanceCustomFields];
    this.updateCustomRequiredFields();
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[]) => {
    this.acceptedConditions = conditions;
  };

  // --- Payment method ---
  setPaymentMethod = (value: string) => {
    this.paymentMethod = value;
    setSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.paymentMethod,
      this.applicationFields
    );
  };

  closePaymentMethodModal = () => {
    this.showPaymentMethodModal = false;
    this.saveTask.perform();
  };

  setSubtotals() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.application;
    const courseCashTotalField = this.isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = this.isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';
    setSingleValueForTasField(
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships,
      this.applicationFields
    );
    setSingleValueForTasField(
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal,
      this.applicationFields
    );
  }

  // --- Education drawer ---
  openEducationDrawer = (course?: TasCourseModel | null) => {
    if (course) {
      this.course = course;
    } else {
      this.course = this.store.createRecord('tas-course', {
        tasApplication: this.application,
        fields: this.programTemplate.courseFieldsHash,
        customFields: this.programTemplate.courseCustomFields,
      });
    }
    this.isEducationOpen = true;
  };

  closeEducationDrawer = () => {
    this.course = null;
    this.isEducationOpen = false;
  };

  // --- Scholarship ---
  toggleScholarshipDrawer = () => {
    this.isScholarshipOpen = !this.isScholarshipOpen;
    if (!this.isScholarshipOpen) {
      this.scholarshipIdToEdit = undefined;
      this.syncScholarshipsFromApplication();
    }
  };

  syncScholarshipsFromApplication() {
    if (this.application.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFields['SCHOLARSHIPS_RECEIVED'] =
        this.application.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  openEditScholarship = (scholarship: ReceivedScholarship) => {
    this.scholarshipIdToEdit = (
      scholarship as ReceivedScholarship & { scholarshipId?: string }
    ).scholarshipId;
    this.isScholarshipOpen = true;
  };

  openDeleteScholarship = (scholarship: ReceivedScholarship) => {
    this.scholarshipToDelete = scholarship;
    this.isDeleteScholarshipOpen = true;
  };

  closeDeleteScholarship = () => {
    delete this.scholarshipToDelete;
    this.isDeleteScholarshipOpen = false;
  };

  deleteScholarship = () => {
    const existingScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.application.fields
    );
    const updatedScholarships = existingScholarships.filter(
      (scholarship: FieldValue) =>
        (scholarship as Record<string, unknown>)?.scholarshipId !==
        this.scholarshipToDelete?.scholarshipId
    ) as ReceivedScholarship[];
    this.application.fields['SCHOLARSHIPS_RECEIVED'] = {
      values: updatedScholarships.filter((scholarship) => scholarship !== null),
    };
    this.application.save();
    this.isDeleteScholarshipOpen = false;
  };

  // --- Delete app/course ---
  onTrash = () => {
    this.isDeleteApplicationOpen = true;
  };

  toggleDeleteApplication = () => {
    this.isDeleteApplicationOpen = !this.isDeleteApplicationOpen;
  };

  toggleDeleteCourse = (course: TasCourseModel | null) => {
    this.isDeleteCourseOpen = !this.isDeleteCourseOpen;
    if (course) {
      this.course = course;
    }
  };

  deleteApplication = dropTask(async () => {
    const appAdapter = this.store.adapterFor('tas-application') as unknown as TasApplicationAdapter;
    const instanceAdapter = this.store.adapterFor(
      'tas-program-instance'
    ) as unknown as TasProgramInstanceAdapter;
    try {
      await appAdapter.abandonCourse(this.application);
      await instanceAdapter.abandonProgram(this.instance);
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error(e);
    }
  });

  deleteCourse = dropTask(async () => {
    if (!this.course) return;
    try {
      if (this.course.isNew) {
        await this.course.rollbackAttributes();
      } else {
        await this.course.destroyRecord();
      }
      this.isDeleteCourseOpen = false;
      this.course = null;
    } catch (e) {
      console.error(e);
    }
  });

  // --- Save / Submit tasks ---
  setSemesterCodeStringOnApplication = () => {
    if (this.programTemplate.semesterCodeIsVisible) {
      const date = getSingleValueForTasField(
        'COURSES_BEGIN_DATE',
        this.applicationFields
      ) as string;
      if (!date) return;
      const semesterCode = setSemesterCodeString(date);
      setSingleValueForTasField('SEMESTER_CODE', semesterCode, this.applicationFields);
    }
  };

  saveTask = dropTask(async () => {
    this.instance.customFields = this.instanceCustomFields;
    this.instance.fields = this.instanceFields;
    this.application.fields = this.applicationFields;
    try {
      await this.application.save();
      await this.instance.save();
    } catch (error) {
      console.error('Failed to save application or instance:', error);
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => agreement.save());
    return await all(promises);
  });

  async prepareTotalsMiscFieldsAndSave() {
    this.setSemesterCodeStringOnApplication();
    if (this.programTemplate.isDirectPayProgram) {
      setSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        this.paymentMethod,
        this.applicationFields
      );
    }
    if (
      this.programTemplate.isDirectPayProgram &&
      this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
    ) {
      this.setSubtotals();
    } else {
      setSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField(
        this.approvedTotalString,
        this.setRequestedTotalAmount,
        this.applicationFields
      );
    }
    await this.saveTask.perform();
  }

  submitApplication = async () => {
    await this.prepareTotalsMiscFieldsAndSave();
    if (!this.canSubmitApplication) return;
    try {
      await this.saveAgreementsForConditions.perform();
      await this.submitTask.perform();
    } catch (error) {
      console.error('Failed to submit application:', error);
    }
  };

  submitTask = dropTask(async () => {
    this.hasSubmitted = true;
    this.submitError = null;
    try {
      const limitsValidationResponse = await this.validateLimits.perform();
      if (!limitsValidationResponse.can_submit) {
        this.displayLimitsValidationError(limitsValidationResponse);
        return;
      }
      const appAdapter = this.store.adapterFor(
        'tas-application'
      ) as unknown as TasApplicationAdapter;
      // adapterMethod falls back to '' when typeClassification isn't recognised;
      // guard so we throw a clear error instead of calling undefined at runtime.
      const adapterMethodName = this.adapterMethod;
      const adapterMethod = adapterMethodName
        ? (appAdapter[adapterMethodName as keyof TasApplicationAdapter] as
            | ((application: TasApplicationModel) => Promise<unknown>)
            | undefined)
        : undefined;
      if (typeof adapterMethod !== 'function') {
        throw new Error(
          `No TAS application adapter method available for typeClassification="${this.typeClassification}"`
        );
      }
      await adapterMethod.call(appAdapter, this.application);

      const typeQueryMap: Record<string, { tasType: string; evidence?: boolean }> = {
        'TAS.ProgramType.2': { tasType: 'two' },
        'TAS.ProgramType.3': { tasType: 'three' },
        'TAS.ProgramType.4.2': { tasType: 'four', evidence: true },
        'TAS.ProgramType.4.3': { tasType: 'four', evidence: false },
      };
      const qp = typeQueryMap[this.typeClassification] || { tasType: 'two' };
      this.router.transitionTo('authenticated.tas.confirmation', {
        queryParams: { ...qp, applicationId: this.application.id },
      });
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  validateLimits = dropTask(async () => {
    return await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
      this.application,
      this.instance.employee
    );
  });

  displayLimitsValidationError(validationResponse = {}) {
    const reductionDisplay = this.application.isCreditBased
      ? // @ts-expect-error: not sure about this one
        `${validationResponse.reduction_needed} credits`
      : // @ts-expect-error: not sure about this one
        this.formatMoney(validationResponse.reduction_needed);
    this.submitError = `You have exceeded your employer's maximum limit. Please reduce your requested amount by ${reductionDisplay}.`;
  }

  formatMoney(cents: number) {
    const dollars = cents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  <template>
    <VStack>
      <Header class="flex justify-between items-center">
        <LinkTo
          class="flex justify-left items-center gap-4 text-sm"
          @route="authenticated.tas.dashboard"
        >
          <PhCaretLeft @size="16" />
          {{t "dashboard"}}
        </LinkTo>
        <div class="flex items-center gap-2">
          <Button
            data-test-save-application
            @intent="primary"
            @appearance="outlined"
            @isRunning={{this.saveTask.isRunning}}
            {{on "click" this.saveTask.perform}}
          >
            {{t "common.save"}}
          </Button>
          {{#if this.showPartnerMessaging}}
            <Button
              @appearance="minimal"
              {{on "click" this.openPartnerMessagingDrawer}}
              class="mb-2"
              data-test-partner-messaging-button
            >
              <PhChatCenteredText @size="32" />
              {{this.numberOfUnreadMessages}}
            </Button>
          {{/if}}
          <Button
            @appearance="minimal"
            @intent="danger"
            class="mb-2 text-danger"
            data-test-delete-application
            {{on "click" this.onTrash}}
          >
            <PhTrash @size="32" />
          </Button>
        </div>
      </Header>

      <div class="mb-4 px-1">
        <h2 class="text-xl font-semibold">{{t "tas.guided.title"}}</h2>
        <p class="text-sm text-gray-500">{{t "tas.guided.description"}}</p>
      </div>

      <HStack class="items-start gap-6">
        <VStack class="w-full lg:basis-2/3">
          {{#each this.applicableSteps key="id" as |step index|}}
            {{#if (this.isStepPassed index)}}
              <CompletedStepSummary
                @stepNumber={{index}}
                @label={{step.label}}
                @summary={{step.summaryText}}
                @summaryKind={{step.summaryKind}}
                @isComplete={{step.isComplete}}
                @onEdit={{fn this.goToStep index}}
              />
            {{else if (eq index this.currentStep)}}
              {{! Active step }}
              {{#if (eq step.id "education-request")}}
                <StepEducationRequest
                  @stepNumber={{index}}
                  @application={{this.application}}
                  @programTemplate={{this.programTemplate}}
                  @instance={{this.instance}}
                  @instanceFields={{this.instanceFields}}
                  @applicationFields={{this.applicationFields}}
                  @startDate={{this.startDate}}
                  @endDate={{this.endDate}}
                  @selectedSchool={{this.selectedSchool}}
                  @eligibility={{this.eligibility}}
                  @eligibleBasedOnWaitingPeriod={{this.eligibleBasedOnWaitingPeriod}}
                  @isActiveParticipant={{this.isActiveParticipant}}
                  @programFormFields={{this.programFormFields}}
                  @customEmployeeFields={{this.customEmployeeFields}}
                  @customProgramFields={{this.customProgramFields}}
                  @customApplicationFields={{this.customApplicationFields}}
                  @touched={{this.touched}}
                  @startDateConfig={{this.startDateConfig}}
                  @endDateConfig={{this.endDateConfig}}
                  @startDateLabel={{this.startDateLabel}}
                  @endDateLabel={{this.endDateLabel}}
                  @startDateValidationErrors={{this.startDateValidationErrors}}
                  @lockStatus={{this.lockStatus}}
                  @eligibilityWaitingPeriod={{this.eligibilityWaitingPeriod}}
                  @dateEligible={{this.dateEligible}}
                  @isLpnSchoolSelected={{this.isLpnSchoolSelected}}
                  @isLpnEnabled={{this.isLpnEnabled}}
                  @showGPAField={{this.showGPAField}}
                  @typeClassification={{this.typeClassification}}
                  @onSaveStartDate={{this.saveStartDate}}
                  @onSaveEndDate={{this.saveEndDate}}
                  @onUpdateAppName={{this.updateAppName}}
                  @onSelectSchool={{this.didSelectSchool}}
                  @onUpdateValueForField={{this.updateValueForField}}
                  @onUpdateDateFieldValue={{this.updateDateFieldValue}}
                  @onDidUpdateEmployeeCustomFields={{this.didUpdateEmployeeCustomFields}}
                  @onDidUpdateProgramCustomFields={{this.didUpdateProgramCustomFields}}
                  @onDidUpdateApplicationCustomFields={{this.didUpdateApplicationCustomFields}}
                  @onSetGPA={{this.setGPA}}
                  @inputValueForField={{this.inputValueForField}}
                  @getSingleValueForField={{this.getSingleValueForField}}
                  @customFieldValidationErrors={{this.customFieldValidationErrors}}
                  @onNext={{this.advanceStep}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                />
              {{else if (eq step.id "expense-items")}}
                <StepExpenseItems
                  @stepNumber={{index}}
                  @application={{this.application}}
                  @expenseTypeField={{this.expenseTypeField}}
                  @onOpenEducationDrawer={{this.openEducationDrawer}}
                  @onToggleDeleteCourse={{this.toggleDeleteCourse}}
                  @onNext={{this.advanceStep}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                  @isComplete={{step.isComplete}}
                />
              {{else if (eq step.id "attachments")}}
                <StepAttachments
                  @stepNumber={{index}}
                  @application={{this.application}}
                  @programTemplate={{this.programTemplate}}
                  @attachmentClassificationString={{this.attachmentClassificationString}}
                  @attachmentsListInstructions={{this.attachmentsListInstructions}}
                  @attachmentsListItems={{this.attachmentsListItems}}
                  @isAttachmentRequired={{this.isAttachmentRequired}}
                  @hasRequiredAttachments={{this.hasRequiredAttachments}}
                  @onNext={{this.advanceStep}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                  @isComplete={{step.isComplete}}
                />
              {{else if (eq step.id "payment-type")}}
                <StepPaymentType
                  @stepNumber={{index}}
                  @application={{this.application}}
                  @programTemplate={{this.programTemplate}}
                  @employee={{this.instance.employee}}
                  @paymentMethod={{this.paymentMethod}}
                  @shouldFetchLimits={{this.shouldFetchLimits}}
                  @scholarships={{this.scholarships}}
                  @scholarshipReceived={{this.scholarshipReceived}}
                  @onSetPaymentMethod={{this.setPaymentMethod}}
                  @onToggleScholarshipDrawer={{this.toggleScholarshipDrawer}}
                  @onOpenDeleteScholarship={{this.openDeleteScholarship}}
                  @onOpenEditScholarship={{this.openEditScholarship}}
                  @isTypeTwoProgram={{this.isTypeTwoProgram}}
                  @onNext={{this.advanceStep}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                  @isComplete={{step.isComplete}}
                />
              {{else if (eq step.id "submit")}}
                <StepSubmit
                  @stepNumber={{index}}
                  @application={{this.application}}
                  @programTemplate={{this.programTemplate}}
                  @conditionsForSubmit={{this.conditionsForSubmit}}
                  @acceptedConditions={{this.acceptedConditions}}
                  @canSubmitApplication={{this.canSubmitApplication}}
                  @isSubmitting={{this.submitTask.isRunning}}
                  @submitError={{this.submitError}}
                  @hasGpaIfRequired={{this.hasGpaIfRequired}}
                  @showGPAField={{this.showGPAField}}
                  @completedSteps={{this.applicableSteps}}
                  @currentStep={{this.currentStep}}
                  @onDidUpdateAcceptedConditions={{this.didUpdateAcceptedConditions}}
                  @onSubmit={{this.submitApplication}}
                  @onEditStep={{this.goToStep}}
                />
              {{/if}}
            {{else}}
              {{! Future step — collapsed }}
              {{#if (this.isReopenableFutureStep index)}}
                <button
                  type="button"
                  class="w-full border border-gray-200 rounded-lg py-4 px-5 flex items-center gap-3 text-left hover:border-violet-300 hover:bg-violet-50/40 transition-colors"
                  {{on "click" (fn this.goToStep index)}}
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-violet-200 text-sm font-medium text-violet-700 bg-white"
                  >{{this.stepDisplayNumber index}}</span>
                  <span class="font-medium text-gray-700">{{step.label}}</span>
                  <span class="ml-auto text-xs text-gray-500">{{step.timeEstimate}}</span>
                </button>
              {{else}}
                <div
                  class="border border-gray-200 rounded-lg py-4 px-5 opacity-50 flex items-center gap-3"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-300 text-sm font-medium text-gray-400"
                  >{{this.stepDisplayNumber index}}</span>
                  <span class="text-gray-400 font-medium">{{step.label}}</span>
                  <span class="ml-auto text-xs text-gray-400">{{step.timeEstimate}}</span>
                </div>
              {{/if}}
            {{/if}}
          {{/each}}
        </VStack>

        <div class="hidden lg:block w-full lg:basis-1/3">
          <div class="sticky top-4">
            <ProgressTracker
              @steps={{this.applicableSteps}}
              @currentStepIndex={{this.currentStep}}
              @furthestVisitedStepIndex={{this.furthestVisitedStepIndex}}
              @onGoToStep={{this.goToStep}}
            />
            {{#if this.isLpnEnabled}}
              <div class="mt-4">
                <LpnInfoCard />
              </div>
            {{/if}}
          </div>
        </div>
      </HStack>

      {{#if this.hasMeaningfulHistory}}
        <div class="w-full lg:w-2/3 lg:pr-3">
          <ApplicationHistoryTimeline
            @history={{this.application.fullHistory}}
            @title={{this.application.applicationName}}
          />
        </div>
      {{/if}}
    </VStack>

    {{! Drawers }}
    {{#if this.isEducationOpen}}
      <AddEducationDrawer
        @isOpen={{this.isEducationOpen}}
        @onClose={{this.closeEducationDrawer}}
        @application={{this.application}}
        @course={{this.course}}
        @header={{this.expenseTypeField}}
        @paymentMethod={{this.paymentMethod}}
        @setPaymentMethod={{this.setPaymentMethod}}
      />
    {{/if}}
    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipOpen}}
      @onClose={{this.toggleScholarshipDrawer}}
      @application={{this.application}}
      @scholarshipIdToEdit={{this.scholarshipIdToEdit}}
    />

    {{! Modals }}
    <Modal
      @isOpen={{this.isDeleteApplicationOpen}}
      @onClose={{this.toggleDeleteApplication}}
      data-test-delete-application-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_application"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_application_delete"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteApplication.isRunning}}
          @onCancel={{this.toggleDeleteApplication}}
          @onSubmit={{this.deleteApplication.perform}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isDeleteCourseOpen}}
      @onClose={{fn this.toggleDeleteCourse null}}
      data-test-delete-course-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_course"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_delete_expense"}}:</p>
        <p class="font-semibold px-2">{{this.course.courseName}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteCourse.isRunning}}
          @onCancel={{fn this.toggleDeleteCourse null}}
          @onSubmit={{this.deleteCourse.perform}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isDeleteScholarshipOpen}}
      @onClose={{this.closeDeleteScholarship}}
      data-test-delete-scholarship-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_scholarship"}}</:header>
      <:body>
        {{#let this.scholarshipToDelete as |scholarship|}}
          {{#if scholarship}}
            <p>{{t "tas.application.type_two.new.confirm_scholarship_delete"}}</p>
            <div class="p-8 flex w-2/3 justify-between">
              <p class="font-medium">{{scholarship.scholarshipName}}</p>
              <p class="font-medium">{{safeFormatNumber
                  (divide scholarship.scholarshipAmount 100)
                  style="currency"
                  currency="USD"
                }}</p>
            </div>
          {{/if}}
        {{/let}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteScholarship}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deleteScholarship}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>

    {{#if this.isPartnerMessagingDrawerOpen}}
      <PartnerMessagingDrawer
        @isOpen={{this.isPartnerMessagingDrawerOpen}}
        @onClose={{this.closePartnerMessagingDrawer}}
        @application={{this.application}}
      />
    {{/if}}

    <PaymentMethodAndSubtotals
      @show={{this.showPaymentMethodModal}}
      @cancel={{this.closePaymentMethodModal}}
      @application={{this.application}}
      @paymentMethod={{this.paymentMethod}}
      @setPaymentMethod={{this.setPaymentMethod}}
    />
  </template>

  stepDisplayNumber = (index: number) => {
    return index + 1;
  };
}
